/**
 * Toucan Music — a library of albums you can play through Spotify. This boots
 * the app: open the store, finish any login Spotify is redirecting back from,
 * and render the shell, which takes it from there.
 */

import { render } from "preact";
import { toucan } from "toucan";
import { Shell } from "./Shell.tsx";
import { handleRedirect, moveToLoopback } from "./connect.tsx";
import { dropUnidentified } from "./model.ts";
import type { Album, Shelf } from "./model.ts";
import { resolveLibrary } from "./musicbrainz.ts";
import { setApp, store } from "./state.ts";

/**
 * Open the store and put the app on the page — the whole of what loading this
 * module does, and the whole of what a browser needs from it.
 *
 * Exported for the tests, which boot the app once per test into a jsdom the
 * files in a worker share. A boot that is only a module's evaluation happens
 * once per module instance, and no test can insist on being given a new one:
 * see `boot()` in `test-helpers.ts` for what the shared registry does with
 * that.
 */
export async function main() {
  // Nothing else is worth doing on an address Spotify will not let this app
  // come back to.
  if (moveToLoopback()) {
    return;
  }
  setApp(
    await toucan.open("music", {
      albums: [] as Album[],
      shelves: [] as Shelf[],
    }),
  );
  dropUnidentified(store());
  // A redirect back from Spotify carries a single-use code; deal with it
  // before the first render so the library shows the connected state.
  await handleRedirect();
  render(<Shell />, document.body);
  // Not awaited: it runs longer the larger the library, and nothing on screen
  // waits for it. What it finishes is kept, and the next boot picks up
  // whatever it did not reach.
  void resolveLibrary();
}

export const ready = main();
